import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  HypothesesHypothesisCarryField as CarryField,
  HypothesesHypothesisCoSubmitter as CoSubmitter,
  HypothesesHypothesisDataPointer as DataPointer,
  HypothesesHypothesisDataPointerPut as DataPointerPut,
  HypothesesHypothesisMemoryCard as MemoryCard,
  HypothesesHypothesisMergeRequest as MergeRequest,
  HypothesesHypothesisMetricExample as MetricExample,
  HypothesesHypothesisRef as HypothesisRef,
} from '../generated/api/types.gen.ts';
import {
  hypothesisInterestAcknowledgeWatch,
  hypothesisInterestDeleteDataPointer,
  hypothesisInterestPutDataPointer,
  hypothesisInterestUnwatch,
  hypothesisInterestWatch,
  hypothesisMemoryMergeCandidates,
  hypothesisMemoryMetricExamples,
  hypothesisMemorySimilar,
} from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';
import { behandlingNoekkel } from './useHypoteseBehandling.ts';
import { hypoteseNoekkel, type HypothesisSummary } from './useHypotheses.ts';

export type { CarryField, CoSubmitter, DataPointer, DataPointerPut, HypothesisRef, MemoryCard, MergeRequest, MetricExample };

/**
 * H3.3 — hukommelsen (H12): «Ligner på N som er vurdert før» mens innsenderen skriver, «Slik har
 * andre tallfestet» i panelet, hypoteser som ligner for vurdereren (veien til sammenslåing), og
 * kollegenes «Varsle meg» og «Jeg har data på dette».
 *
 * Søkene leser det LAGREDE utkastet hos tjeneren: teksten havner aldri i en adresse. Nøklene eies
 * her og bare her, og deler prefikset `hypotheses`.
 */
export const hukommelseNoekkel = {
  ligner: (id: string, runde: string) => ['hypotheses', 'ligner', id, runde] as const,
  eksempler: (id: string) => ['hypotheses', 'eksempler', id] as const,
  kandidater: (id: string) => ['hypotheses', 'kandidater', id] as const,
};

/** «Ligner på» er et tilbud: svarer ikke tjeneren innen ett sekund, står ingenting (W1). */
export const LIGNER_FRIST_MS = 1000;

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/** En frist som feiler kallet stille — søket blir et tomt tilbud, aldri en feilmelding. */
function medFrist<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((ok, nei) => {
    const t = setTimeout(() => nei(new Error('ligner_for_treg')), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        ok(v);
      },
      (e: unknown) => {
        clearTimeout(t);
        nei(e);
      },
    );
  });
}

/**
 * «Ligner på» for innsenderens utkast. `runde` er leddet som står åpent: søket kjøres på nytt når
 * personen går videre (og på «Les over»), aldri for hvert tastetrykk. Forrige treff står mens det
 * neste hentes, så kortene ikke blinker.
 */
export function useLignerPaa(id: string | null, runde: string, enabled = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: hukommelseNoekkel.ligner(id ?? '', runde),
    enabled: enabled && id !== null,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<MemoryCard[]> => {
      const res = await medFrist(hypothesisMemorySimilar({ client: klient, path: { id: id! } }), LIGNER_FRIST_MS);
      if (!res.response?.ok || !res.data) kast(res);
      return (res.data as { items: MemoryCard[] }).items;
    },
  });
}

/** «Slik har andre tallfestet» — konkluderte hypoteser med et lignende måltall. */
export function useSlikHarAndreTallfestet(id: string | null, enabled = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: hukommelseNoekkel.eksempler(id ?? ''),
    enabled: enabled && id !== null,
    queryFn: async (): Promise<MetricExample[]> => {
      const res = await hypothesisMemoryMetricExamples({ client: klient, path: { id: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return (res.data as { items: MetricExample[] }).items;
    },
  });
}

/** Åpne hypoteser som ligner, for vurdereren — maks tre, et forslag og aldri en sammenslåing av seg selv. */
export function useSammenslaaingKandidater(id: string | null, enabled = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: hukommelseNoekkel.kandidater(id ?? ''),
    enabled: enabled && id !== null,
    queryFn: async (): Promise<HypothesisSummary[]> => {
      const res = await hypothesisMemoryMergeCandidates({ client: klient, path: { id: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return (res.data as { items: HypothesisSummary[] }).items;
    },
  });
}

/** Etter «Varsle meg», «OK» eller et bidrag: hypotesen, «Ligner på»-kortene, lista og merket. */
function friskOpp(queryClient: ReturnType<typeof useQueryClient>, id: string) {
  void queryClient.invalidateQueries({ queryKey: hypoteseNoekkel.detalj(id) });
  void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'nummer'] });
  void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'liste'] });
  void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'ligner'] });
  void queryClient.invalidateQueries({ queryKey: behandlingNoekkel.counts });
}

/**
 * «Varsle meg» og «Slutt å varsle». Ingen logg-rad og ingen telling (Kh8): det er den innloggedes
 * eget valg, og bare hun ser det.
 */
export function useVarsleMeg() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, paa }: { id: string; paa: boolean }): Promise<void> => {
      const res = paa ? await hypothesisInterestWatch({ client: klient, path: { id } }) : await hypothesisInterestUnwatch({ client: klient, path: { id } });
      if (!res.response?.ok) kast(res);
    },
    onSuccess: (_v, { id }) => friskOpp(queryClient, id),
  });
}

/** «OK» i «Til deg»: den fulgte hypotesen går ut av lista. Lesing gjør det aldri (Kh9). */
export function useKvitterVarsel() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await hypothesisInterestAcknowledgeWatch({ client: klient, path: { id } });
      if (!res.response?.ok) kast(res);
    },
    onSuccess: (_v, id) => friskOpp(queryClient, id),
  });
}

/** «Jeg har data på dette» — ett bidrag per person; et nytt erstatter det forrige. */
export function useLagreDatapeker() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: DataPointerPut }): Promise<DataPointer> => {
      const res = await hypothesisInterestPutDataPointer({ client: klient, path: { id }, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as DataPointer;
    },
    onSuccess: (_d, { id }) => friskOpp(queryClient, id),
  });
}

/** Tar den innloggedes eget bidrag ut igjen. */
export function useFjernDatapeker() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await hypothesisInterestDeleteDataPointer({ client: klient, path: { id } });
      if (!res.response?.ok) kast(res);
    },
    onSuccess: (_v, id) => friskOpp(queryClient, id),
  });
}
